import React, { useState, useEffect, useRef } from 'react';
import { Input, Label } from './ui';
import { useFormContext } from '../context/FormContext';

// Formatea la parte entera agregando puntos cada tres dígitos
const formatInteger = (digits) => {
	const clean = String(digits).replace(/\D/g, '').replace(/^0+(?=\d)/, '');
	return clean.replace(/\B(?=(\d{3})+(?!\d))/g, '.') || '0';
};

// number -> "$1.234,56"
const formatNumberToDisplay = (value) => {
	if (value === null || value === undefined || value === '') return '';
	const n = Number(value);
	if (isNaN(n)) return '';
	const [intPart, decPart] = Math.abs(n).toFixed(2).split('.');
	const sign = n < 0 ? '-' : '';
	return `$${sign}${formatInteger(intPart)},${decPart}`;
};

const toNumberOrNull = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const n = Number(value);
	return isNaN(n) ? null : n;
};

// "$1.234,56" -> 1234.56
const parseDisplayToNumber = (display) => {
	const cleaned = String(display).replace(/[^\d,]/g, '');
	if (!/\d/.test(cleaned)) return null;
	const [intPart, decPart = ''] = cleaned.split(',');
	const intDigits = intPart.replace(/\D/g, '') || '0';
	const decDigits = decPart.replace(/\D/g, '').slice(0, 2);
	return parseFloat(`${intDigits}.${decDigits || '0'}`);
};

const InputMontoObra = () => {
	const { formA, loadField, markSectionDirty } = useFormContext();
	const { datosGenerales = {} } = formA;

	const [display, setDisplay] = useState(formatNumberToDisplay(datosGenerales.monto_obra));
	const lastEmitted = useRef(toNumberOrNull(datosGenerales.monto_obra));

	// Sincronizar solo cuando el valor cambia desde afuera (no por nuestra propia edición)
	useEffect(() => {
		const incoming = toNumberOrNull(datosGenerales.monto_obra);
		if (incoming !== lastEmitted.current) {
			lastEmitted.current = incoming;
			setDisplay(formatNumberToDisplay(incoming));
		}
	}, [datosGenerales.monto_obra]);

	const handleChange = (e) => {
		markSectionDirty('formA');
		const input = e.target;
		const rawBefore = input.value;
		const caret = input.selectionStart ?? rawBefore.length;

		// Cantidad de dígitos antes del cursor (ignorando $ , y puntos)
		const digitsBefore = rawBefore.slice(0, caret).replace(/\D/g, '').length;

		const rawCommaIndex = rawBefore.indexOf(',');
		const inDecimalZone = rawCommaIndex !== -1 && caret > rawCommaIndex;
		const decDigitsBefore = inDecimalZone
			? rawBefore.slice(rawCommaIndex + 1, caret).replace(/\D/g, '').length
			: 0;

		const cleaned = rawBefore.replace(/[^\d,]/g, '');
		const hasDigits = /\d/.test(cleaned);
		const commaIndex = cleaned.indexOf(',');

		let intDigits;
		let decDigits = null;
		if (commaIndex === -1) {
			intDigits = cleaned.replace(/\D/g, '');
		} else {
			intDigits = cleaned.slice(0, commaIndex).replace(/\D/g, '');
			decDigits = cleaned.slice(commaIndex + 1).replace(/\D/g, '').slice(0, 2);
		}

		let newDisplay;
		if (!hasDigits && commaIndex === -1) {
			newDisplay = '';
		} else if (decDigits === null) {
			newDisplay = `$${formatInteger(intDigits)}`;
		} else {
			newDisplay = `$${formatInteger(intDigits)},${decDigits}`;
		}

		// Restaurar el cursor contando dígitos en el nuevo string
		let target;
		const newCommaIndex = newDisplay.indexOf(',');
		if (inDecimalZone && newCommaIndex !== -1) {
			// El cursor estaba en la parte decimal: ubicarlo tras la misma cantidad de decimales
			if (decDigitsBefore <= 0) {
				target = newCommaIndex + 1;
			} else {
				let seen = 0;
				target = newDisplay.length;
				for (let i = newCommaIndex + 1; i < newDisplay.length; i++) {
					if (/\d/.test(newDisplay[i])) {
						seen++;
						if (seen === decDigitsBefore) { target = i + 1; break; }
					}
				}
			}
		} else if (digitsBefore <= 0) {
			target = newDisplay.startsWith('$') ? 1 : 0;
		} else {
			let seen = 0;
			target = newDisplay.length;
			for (let i = 0; i < newDisplay.length; i++) {
				if (/\d/.test(newDisplay[i])) {
					seen++;
					if (seen === digitsBefore) { target = i + 1; break; }
				}
			}
		}

		setDisplay(newDisplay);

		const num = parseDisplayToNumber(newDisplay);
		lastEmitted.current = num;
		loadField('formA', 'datosGenerales', 'monto_obra', num ?? '');

		setTimeout(() => {
			input.setSelectionRange(target, target);
		}, 0);
	};

	return (
		<div>
			<Label htmlFor="monto_obra">Monto de obra</Label>
			<Input
				id="monto_obra"
				type="text"
				inputMode="decimal"
				value={display}
				onChange={handleChange}
				placeholder="$ Ej: 1.234,56"
			/>
		</div>
	);
};

export default InputMontoObra;
